import React, { useState } from 'react'
import { Flex, TextVariant, Text, Tooltip } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { ChevronDown, Info } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { minAmountOut } from 'utils/swap/rank'
import { IMPACT_CONFIRM_BPS, IMPACT_WARN_BPS } from 'utils/swap/types'
import type { Quote, SwapSettings, SwapToken } from 'utils/swap/types'
import { RefreshPlaceholder, RotatingRefreshCcw } from '../../General/Refresh'

const QUOTE_STALE_MS = 15_000

/**
 * The collapsed summary under the fields, restored from the exchange's own
 * Swap tab: the rate on one line, everything else a chevron away.
 *
 * Two of the rows are new, and they are the two that decide whether a trade is
 * a good idea. **Minimum received** is the honest number — it is what the
 * transaction enforces, while the estimate above it is a hope about a price
 * that is still moving. **Price impact** is measured by asking the same route
 * for a thousandth of the size, so it says what this trade costs by being this
 * big rather than what a model thinks a pool should do. Above five per cent it
 * turns amber and above fifteen it turns red, and at that point the button
 * will not fire until the user says they meant it.
 *
 * The old tab's network-fee row is gone rather than left showing a dash: gas
 * on Base is fractions of a cent and no longer worth a line, and it is not
 * part of the ranking (recorded as a known simplification in the scope).
 */
const Wrapper = styled(Flex)`
  width: 100%;
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 8px;
  align-items: center;
  background: ${({ theme }) => theme.colors.gray10};
`

const DetailsWrapper = styled(Flex)<{ showDetails: boolean }>`
  overflow: hidden;
  transition: all 0.4s ease-in-out;
  max-height: ${({ showDetails }) => (showDetails ? '100%' : '0')};
`

const RowDetails = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  gap: 12px;
`

const RowWrapper = styled(Flex)`
  gap: 8px;
  align-items: center;
`

const Chevron = styled(Flex)<{ open: boolean }>`
  cursor: pointer;
  min-height: 44px;
  align-items: center;
  transition: transform 0.3s ease-in-out;
  transform: rotate(${({ open }) => (open ? '180deg' : '0deg')});
`

const StyledDetailsText = styled(Text).attrs(() => ({
  variant: TextVariant.TEXT_XS_BOLD
}))<{ description?: boolean }>`
  color: ${({ theme, description }) => (description ? theme.colors.gray1 : theme.colors.gray6)};
`

function format(amount: bigint, decimals: number): string {
  return Number(ethers.utils.formatUnits(amount.toString(), decimals)).toLocaleString(undefined, {
    maximumFractionDigits: 6
  })
}

interface SwapSummaryProps {
  quote: Quote | null
  tokenIn: SwapToken
  tokenOut: SwapToken | null
  settings: SwapSettings
  routing: string
  /** MPRO's burn for this account, in basis points; zero when it does not apply. */
  burnBps: number
  quotedAt: number | null
  now: number
  loading: boolean
  onRefresh: () => void
}

const SwapSummary: React.FC<SwapSummaryProps> = ({
  quote,
  tokenIn,
  tokenOut,
  settings,
  routing,
  burnBps,
  quotedAt,
  now,
  loading,
  onRefresh
}) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const [showDetails, setShowDetails] = useState(false)

  if (!quote || !tokenOut) return null

  const minOut = minAmountOut(quote.amountOut, settings.slippageBps)
  const impact = quote.priceImpactBps
  const impactColor =
    impact === undefined
      ? colors.gray1
      : impact >= IMPACT_CONFIRM_BPS
      ? colors.red
      : impact >= IMPACT_WARN_BPS
      ? colors.yellow
      : colors.gray1

  // The rate is what the user pays against what they get — gross, fee included.
  const paid = quote.grossAmountIn ?? quote.amountIn
  const rate =
    paid === 0n
      ? null
      : Number(ethers.utils.formatUnits(quote.amountOut.toString(), tokenOut.decimals)) /
        Number(ethers.utils.formatUnits(paid.toString(), tokenIn.decimals))

  const stale = quotedAt !== null && now - quotedAt > QUOTE_STALE_MS

  return (
    <Flex flexDirection="column" fullWidth gap="2px">
      <Wrapper justifyContent="space-between">
        <RowWrapper>
          <Tooltip title={t('swap.conversionRate')}>
            <Info color={colors.gray6} style={{ cursor: 'pointer' }} />
          </Tooltip>
          {loading || rate === null ? (
            <RefreshPlaceholder refresh width="120px" />
          ) : (
            <Flex gap="8px" alignItems="center">
              <Text variant={TextVariant.TEXT_XS_BOLD} color={stale ? colors.gray6 : colors.white}>
                {`1 ${tokenIn.symbol} = ${rate.toLocaleString(undefined, {
                  maximumSignificantDigits: 8
                })} ${tokenOut.symbol}`}
              </Text>
              <RotatingRefreshCcw refresh={loading} color={colors.gray2} onClick={onRefresh} />
            </Flex>
          )}
        </RowWrapper>
        <Chevron open={showDetails} onClick={() => setShowDetails(!showDetails)}>
          <ChevronDown color={colors.gray2} size={18} />
        </Chevron>
      </Wrapper>
      <DetailsWrapper showDetails={showDetails}>
        <Wrapper flexDirection="column" gap="16px">
          <RowDetails>
            <StyledDetailsText>{t('swap.orderRouting')}</StyledDetailsText>
            <StyledDetailsText description>{routing}</StyledDetailsText>
          </RowDetails>
          <RowDetails>
            <StyledDetailsText>{t('swap.minimumReceived')}</StyledDetailsText>
            <StyledDetailsText description>
              {`${format(minOut, tokenOut.decimals)} ${tokenOut.symbol} (${(settings.slippageBps / 100).toFixed(2)} %)`}
            </StyledDetailsText>
          </RowDetails>
          {quote.feeIn !== undefined && quote.feeBps !== undefined ? (
            <RowDetails>
              <RowWrapper>
                <StyledDetailsText>{t('swap.platformFee')}</StyledDetailsText>
                <Tooltip title={t('swap.platformFeeTooltip')}>
                  <Info color={colors.gray6} size={14} style={{ cursor: 'pointer' }} />
                </Tooltip>
              </RowWrapper>
              <StyledDetailsText description>
                {quote.feeIn === 0n
                  ? t('swap.platformFeeNone')
                  : `${format(quote.feeIn, tokenIn.decimals)} ${tokenIn.symbol} (${(quote.feeBps / 100).toFixed(2)} %)`}
              </StyledDetailsText>
            </RowDetails>
          ) : null}
          <RowDetails>
            <StyledDetailsText>{t('swap.priceImpact')}</StyledDetailsText>
            <Text variant={TextVariant.TEXT_XS_BOLD} color={impactColor}>
              {impact === undefined ? t('swap.impactUnknown') : `${(impact / 100).toFixed(2)} %`}
            </Text>
          </RowDetails>
        </Wrapper>
      </DetailsWrapper>
      {impact === undefined ? (
        /*
         * An unmeasured impact gets the same prominence as a bad one. It is
         * not the neutral case: it turns up when no reference quote could be
         * had, which in practice means the aggregator was unreachable and the
         * only route left is a thin pool — the exact combination that let a
         * trade for an eighth of a pool's reserve through on 2026-09-09.
         */
        <Wrapper>
          <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.yellow}>
            {t('swap.impactUnknownWarning')}
          </Text>
        </Wrapper>
      ) : impact >= IMPACT_WARN_BPS ? (
        <Wrapper>
          <Text variant={TextVariant.TEXT_XS_BOLD} color={impactColor}>
            {impact >= IMPACT_CONFIRM_BPS ? t('swap.impactSevere') : t('swap.impactHigh')}
          </Text>
        </Wrapper>
      ) : null}
      {burnBps > 0 ? (
        <Wrapper>
          <Text variant={TextVariant.TEXT_XS_BOLD} color={colors.yellow}>
            {t('swap.burnNotice', { percent: (burnBps / 100).toFixed(2) })}
          </Text>
        </Wrapper>
      ) : null}
    </Flex>
  )
}

export default SwapSummary
